import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { XCircleIcon, CheckCircle2Icon, MailIcon } from 'lucide-react';
import { PAIN_INTRO, PAINS, STORY_BLOCKS, AUTHOR, PROBLEM_SECTION, SOLUTION_INTRO } from '../constants/content';
import { CtaButton } from './CtaButton';
function Reveal({
  children
}: {
  children: React.ReactNode;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5
  }} data-id="element-188">
      {children}
    </motion.div>;
}
export function SalesLetter() {
  return <section className="w-full bg-cream py-14 sm:py-20" data-id="element-189">
      <div className="mx-auto max-w-[800px] px-4" data-id="element-190">
        {/* Letter header */}
        <Reveal data-id="element-191">
          <div className="mb-8 border-l-4 border-crimson bg-white p-5 shadow-sm" data-id="element-192">
            <p className="flex items-center gap-2 text-sm font-600 uppercase tracking-wide text-crimson" data-id="element-193">
              <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-194" />
              {PAIN_INTRO.from}
            </p>
            <p className="mt-1 text-sm italic text-gray-600" data-id="element-195">{PAIN_INTRO.re}</p>
          </div>
        </Reveal>

        <Reveal data-id="element-196">
          <p className="mb-2 text-lg font-700" data-id="element-197">{PAIN_INTRO.greeting}</p>
          <p className="mb-6 font-display text-2xl font-600 text-ink" data-id="element-198">
            {PAIN_INTRO.question}
          </p>
        </Reveal>

        {/* Pain points */}
        <ul className="mb-8 space-y-3" data-id="element-199">
          {PAINS.map((pain, i) => <motion.li key={i} initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className="flex items-start gap-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-100" data-id="element-200">
              <XCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-crimson" aria-hidden="true" data-id="element-201" />
              <span className="text-base leading-relaxed text-gray-800" data-id="element-202">
                {pain}
              </span>
            </motion.li>)}
        </ul>

        {STORY_BLOCKS.map((block, i) => <Reveal key={i} data-id="element-203">
            <p className="mb-4 text-lg leading-relaxed text-gray-800" data-id="element-204">
              {block}
            </p>
          </Reveal>)}

        <Reveal data-id="element-205">
          <p className="my-8 text-center font-display text-2xl font-600 leading-snug text-crimson sm:text-3xl" data-id="element-206">
            «¡Tener una pollería rentable no debería ser tan difícil!»
          </p>
        </Reveal>

        <Reveal data-id="element-207">
          <div className="space-y-4 text-lg leading-relaxed text-gray-800" data-id="element-208">
            <p data-id="element-209">
              Ahora bien, si querés montar tu propio local — o hacer por fin
              rentable el que ya tenés — de forma rápida y sin vueltas…
            </p>
            <p data-id="element-210">
              En lugar de perder meses o años a los ponchazos, aprendiendo a las
              patadas con la plata que tanto te costó juntar…
            </p>
            <p data-id="element-211">
              Y si soñás con la satisfacción de manejar tu propio negocio, con
              tus propias reglas, sin depender de nadie…
            </p>
            <p className="font-600" data-id="element-212">
              Entonces esta puede ser la carta más importante que leas este año…
            </p>
          </div>
        </Reveal>

        {/* Author card */}
        <Reveal data-id="element-213">
          <div className="my-10 flex flex-col items-center gap-4 rounded-2xl bg-white p-6 text-center shadow-md ring-1 ring-black/5 sm:flex-row sm:text-left" data-id="element-214">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-crimson-dark font-display text-4xl font-700 text-gold-light ring-4 ring-gold/40" role="img" aria-label="Isotipo de Franco R." data-id="element-215">
              F
            </div>
            <div data-id="element-216">
              <p className="text-base leading-relaxed text-gray-600" data-id="element-217">
                Comerciante de toda la vida. Pasé de 2 cajones de pollo a tener
                mi propio local. Creador de Pollería Rentable 2026.
              </p>
            </div>
          </div>
        </Reveal>

        {/* The big problem */}
        <Reveal data-id="element-218">
          <h2 className="mb-4 font-display text-3xl font-700 text-ink sm:text-4xl" data-id="element-219">
            «{PROBLEM_SECTION.title}»
          </h2>
          <p className="mb-2 text-lg leading-relaxed text-gray-800" data-id="element-220">
            {PROBLEM_SECTION.intro}
          </p>
          <p className="mb-4 text-lg font-700 text-crimson" data-id="element-221">
            {PROBLEM_SECTION.subtitle}
          </p>
        </Reveal>

        <ul className="mb-8 space-y-3" data-id="element-222">
          {PROBLEM_SECTION.bullets.map((b, i) => <li key={i} className="flex items-start gap-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-100" data-id="element-223">
              <XCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-crimson" aria-hidden="true" data-id="element-224" />
              <span className="text-base leading-relaxed text-gray-800" data-id="element-225">
                {b}
              </span>
            </li>)}
        </ul>

        <Reveal data-id="element-226">
          <p className="mb-8 text-lg leading-relaxed text-gray-800" data-id="element-227">
            Y en la mayoría de los casos, directamente no hay ningún detalle
            real.
          </p>
          <div className="space-y-4 text-lg leading-relaxed text-gray-800" data-id="element-228">
            {PROBLEM_SECTION.outro.map((p, i) => <p key={i} data-id="element-229">{p}</p>)}
          </div>
        </Reveal>

        <Reveal data-id="element-230">
          <p className="my-8 text-center font-display text-2xl font-600 leading-snug text-ink sm:text-3xl" data-id="element-231">
            ¡Con razón parece tan complicado abrir tu pollería!
          </p>
        </Reveal>

        {/* The solution */}
        <Reveal data-id="element-232">
          <h2 className="mb-4 font-display text-3xl font-700 text-ink sm:text-4xl" data-id="element-233">
            {SOLUTION_INTRO.title}
          </h2>
          <div className="space-y-4 text-lg leading-relaxed text-gray-800" data-id="element-234">
            {SOLUTION_INTRO.paragraphs.map((p, i) => <p key={i} className="flex items-start gap-3" data-id="element-235">
                <CheckCircle2Icon className="mt-1 h-6 w-6 shrink-0 text-green-600" aria-hidden="true" data-id="element-236" />
                <span data-id="element-237">{p}</span>
              </p>)}
          </div>
        </Reveal>

        <Reveal data-id="element-238">
          <div className="my-8 flex justify-center" data-id="element-239">
            <div className="w-full max-w-md" data-id="element-240">
              <CtaButton data-id="element-241">COMPRAR AHORA</CtaButton>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}